import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import day from './lib/day.jpg'; 
import night from './lib/night.jpg';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import CurrentWeather from './components/CurrentWeather';
import Forecast from './components/Forecast';
import { useWeatherData } from './hooks/useWeatherData';
import { useGeolocation } from './hooks/useGeolocation';
import { getLocationFromCoordinates } from './services/weatherService';
import { isDaytime } from './utils/timeService';
import { CONFIG, TEMPERATURE_UNITS } from './constants';

const App = () => {
  const {
    weather,
    forecast,
    location,
    lastVisited,
    loading,
    error,
    updateLocation,
    clearHistory,
  } = useWeatherData();

  const { coordinates, getLocation } = useGeolocation();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [temperatureUnit, setTemperatureUnit] = useState(TEMPERATURE_UNITS.CELSIUS);

  // Update current time
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Handle geolocation coordinates
  useEffect(() => {
    const handleCoordinates = async () => {
      if (coordinates) {
        try {
          const locationName = await getLocationFromCoordinates(
            coordinates.latitude,
            coordinates.longitude
          );
          updateLocation(locationName);
          setIsSidebarOpen(false);
        } catch (error) {
          toast.error('Unable to get location name');
        }
      }
    };
    handleCoordinates();
  }, [coordinates]);

  const handleLocationSelect = (newLocation) => {
    updateLocation(newLocation);
    setIsSidebarOpen(false);
  };

  const handleUnitToggle = () => {
    setTemperatureUnit(prev => 
      prev === TEMPERATURE_UNITS.CELSIUS 
        ? TEMPERATURE_UNITS.FAHRENHEIT 
        : TEMPERATURE_UNITS.CELSIUS
    );
  };


  return (
    <div
      className="relative min-h-screen text-white bg-cover bg-center bg-no-repeat"
      style={{ 
        backgroundImage: `url(${isDaytime(currentTime) ? day : night})` 
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black opacity-50"></div>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white bg-opacity-10 backdrop-blur-md"
        aria-label="Toggle sidebar menu"
        aria-expanded={isSidebarOpen}
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className="h-6 w-6" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M4 6h16M4 12h16m-16 6h16" 
          />
        </svg>
      </button>

      <div className="relative flex flex-col md:flex-row min-h-screen">
        {/* Sidebar */}
        <Sidebar
          isSidebarOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          onLocationSelect={handleLocationSelect}
          lastVisited={lastVisited}
          onClearHistory={clearHistory}
          onGetLocation={getLocation}
        />

        {/* Main Content */}
        <div className="flex-1 p-6 md:p-10">
          <div className="relative flex-1 p-4 md:p-10 overflow-y-auto">
            <Header currentTime={currentTime} />

            {loading ? (
              <div className="flex justify-center items-center min-h-[400px]">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent" 
                     role="status" 
                     aria-label="Loading weather data"
                />
              </div>
            ) : (
              <div className="space-y-8">
                {/* Current Weather */}
                <CurrentWeather 
                  weather={weather} 
                  unit={temperatureUnit}
                  onUnitToggle={handleUnitToggle}
                />

                {/* Forecast */}
                {weather && (
                  <Forecast 
                    forecast={forecast} 
                    unit={temperatureUnit}
                  />
                )}
              </div>
            )}
          </div>
          <footer className="text-center md:text-right text-xs mt-8 md:absolute md:bottom-0 md:right-0 md:mb-4 md:mr-4">
            &copy; 2024 All Rights Reserved | Developed By Mayank with ❤️
          </footer>
        </div>
      </div>

      <ToastContainer
        position="bottom-right"
        autoClose={CONFIG.TOAST_AUTO_CLOSE}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
};

export default App;